import type {Item} from './infra';

export function templateInputFacts(input: Item): {label:string; value:string}[] {
  const facts = [{label:'입력 방식', value:input.generateSecret ? '비밀값 자동 생성' : input.generate ? '범위 내 자동 생성' : input.required ? '필수 입력' : '선택 입력'}];
  if (input.advanced) facts.push({label:'표시 위치',value:'고급 설정'});
  if (input.type !== 'secret' && input.default !== undefined && input.default !== null) facts.push({label:'기본값',value:String(input.default)});
  for (const [key,label] of [['minimum','최솟값'],['maximum','최댓값'],['minLength','최소 글자 수'],['maxLength','최대 글자 수'],['pattern','입력 패턴']]) {
    if (input[key] !== undefined) facts.push({label,value:String(input[key])});
  }
  if (input.integer) facts.push({label:'숫자 형식',value:'정수'});
  if (input.options?.length) facts.push({label:'선택지',value:input.options.join(', ')});
  return facts;
}

const composeLabels: Record<string,string> = {
  ports:'공개 포트', volumes:'저장소 연결', environment:'환경변수', depends_on:'시작 의존성',
  healthcheck:'상태 검사', restart:'재시작 정책', command:'실행 명령', entrypoint:'시작 명령',
  networks:'네트워크', working_dir:'작업 경로', user:'실행 사용자', labels:'라벨',
};

export function templateConfiguration(template: Item | null): {components: Item[]; volumes: string[]; networks: string[]} {
  const config = template?.['configuration'] || {};
  const format = (value: any): string => {
    if (value === null || value === undefined) return '기본값';
    if (Array.isArray(value)) return value.map(format).join('\n');
    if (typeof value === 'object') return Object.entries(value).map(([key,v]) => `${key}: ${format(v)}`).join('\n');
    return String(value);
  };
  return {
    components: Object.entries(config.services || {}).map(([name,value]) => {
      const service = value as Item;
      return {name,image:service.image,fields:Object.entries(service).filter(([key]) => key !== 'image')
        .map(([key,value]) => ({label:composeLabels[key] || key,value:format(value)}))};
    }),
    volumes:Object.keys(config.volumes || {}),
    networks:Object.keys(config.networks || {}).length ? Object.keys(config.networks) : ['기본 bridge 네트워크'],
  };
}

export function templateInputSummary(input: Item): string {
  const type: Record<string,string> = {secret:'비밀번호',number:'숫자',boolean:'사용 여부',enum:'선택',string:'텍스트'};
  const details = [type[input.type] || input.type || '텍스트',input.generateSecret ? '비밀값 자동 생성' : input.generate ? '범위 내 자동 생성' : input.required ? '필수 입력' : '선택 입력'];
  if (input.advanced) details.push('고급 설정');
  if (input.type !== 'secret' && input.default !== undefined && input.default !== null) details.push(`기본값: ${input.default}`);
  if (input.minLength !== undefined) details.push(`최소 ${input.minLength}자`);
  if (input.maxLength !== undefined) details.push(`최대 ${input.maxLength}자`);
  if (input.minimum !== undefined) details.push(`최솟값 ${input.minimum}`);
  if (input.maximum !== undefined) details.push(`최댓값 ${input.maximum}`);
  if (input.options?.length) details.push(`선택지: ${input.options.join(', ')}`);
  return details.join(' · ');
}

export function templateFields(template: Item | undefined, advanced = false): Item[] {
  return (template?.['inputs'] || []).filter((input: Item) => {
    const hasDefault = input['default'] !== undefined && input['default'] !== null && String(input['default']).trim() !== '';
    const optional = input['advanced'] === true && (!input['required'] || hasDefault || input['generate'] === true);
    return !input['generateSecret'] && optional === advanced;
  });
}

export function templateDefaults(template: Item): Item {
  return Object.fromEntries((template['inputs'] || [])
    .filter((input: Item) => input['type'] !== 'secret' && input['default'] !== undefined)
    .map((input: Item) => [input['key'], input['default']]));
}

export function templateInputError(template: Item | undefined, values: Item): string {
  for (const input of template?.['inputs'] || []) {
    const label = input.label || input.key;
    if (input.generateSecret) {
      if (input.type !== 'secret') return `${label}: generateSecret은 secret 입력에 사용하세요.`;
      continue;
    }
    if (input.generate) {
      if (input.type !== 'number') return `${label}: generate는 number 입력에 사용하세요.`;
      if (typeof input.minimum !== 'number' || typeof input.maximum !== 'number' || !Number.isFinite(input.minimum) || !Number.isFinite(input.maximum) || input.minimum > input.maximum) return `${label}: 자동 생성의 minimum과 maximum을 확인하세요.`;
      if (input.integer && Math.ceil(input.minimum) > Math.floor(input.maximum)) return `${label}: 범위 안에 생성할 정수가 없습니다.`;
    }
    let value = values[input.key];
    if (value === undefined || value === null || (input.generate && String(value).trim() === '')) value = input.default;
    if (input.generate && (value === undefined || value === null || String(value).trim() === '')) continue;
    const text = String(value ?? '');
    if (!text.trim() && input.required) return `${label}을(를) 입력해 주세요.`;
    if (!text && input.type === 'secret' && !input.required) continue;
    if (/[\u0000-\u001f\u007f-\u009f\u2028\u2029]/u.test(text)) return `${label}: 줄바꿈과 제어문자는 사용할 수 없습니다.`;
    const length = Array.from(text).length;
    if (input.minLength !== undefined && length < input.minLength) return `${label}: ${input.minLength}자 이상 입력하세요.`;
    if (length > (input.maxLength ?? 4096)) return `${label}: ${input.maxLength ?? 4096}자 이하로 입력하세요.`;
    if (input.pattern) {
      try { if (!new RegExp(`^(?:${input.pattern})$`, 'u').test(text)) return `${label}: ${input.description || '입력 형식을 확인하세요.'}`; }
      catch { return `${label}: 템플릿의 검증 패턴을 확인하세요.`; }
    }
    if (input.type === 'number') {
      const number = Number(text);
      if (!text.trim() || !Number.isFinite(number)) return `${label}: 숫자를 입력하세요.`;
      if (input.integer && !Number.isInteger(number)) return `${label}: 정수를 입력하세요.`;
      if (input.minimum !== undefined && number < input.minimum) return `${label}: ${input.minimum} 이상이어야 합니다.`;
      if (input.maximum !== undefined && number > input.maximum) return `${label}: ${input.maximum} 이하여야 합니다.`;
    }
    if (input.type === 'enum' && !(input.options || []).map(String).includes(text)) return `${label}: 허용된 값을 선택하세요.`;
  }
  return '';
}

export function serviceNameError(name: unknown): string {
  return /^[a-z0-9][a-z0-9-]{1,79}$/.test(String(name ?? '')) ? '' : '서비스 이름은 영문 소문자, 숫자, 하이픈으로 2~80자 입력하세요.';
}
